import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { uniqBy } from 'lodash-es';
import GridViewIcon from '@mui/icons-material/GridView';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';

import { boardsServices } from '@services';
import { showError } from '@utils';
import { BOARDS_ID } from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';
import { MY_ISSUES_MAPPER } from 'components/myIssues/constants';
import { GENERIC_TICKET_REASON_OPTIONS } from 'components/genericTickets/constants';
import { handleMyBoardsData, COMPLETED_STATUSES } from './utils';

const useMyIssuesFilterConfig = () => {
  const router = useRouter();

  const { data: taskStatusesMapperData, error: taskStatusesMapperError } =
    useQuery(
      [QUERY_KEYS.GET_BOARDS_TASK_STATUSES_KEY],
      () => boardsServices.getBoardsEnum(),
      {
        enabled: router.isReady,
      }
    );

  const { data: boardsData, error: boardsDataError } = useQuery(
    [QUERY_KEYS.GET_MY_BOARDS_KEY],
    () => boardsServices.getMyBoards(),
    {
      enabled: router.isReady,
    }
  );

  const { boardsOptions, boardStatusesOptions } = useMemo(
    () =>
      handleMyBoardsData(
        boardsData?.data || [],
        taskStatusesMapperData?.data?.taskStatuses || []
      ),
    [boardsData?.data, taskStatusesMapperData?.data?.taskStatuses]
  );

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      board: parseQueryValue(router.query?.[MY_ISSUES_MAPPER.board.key]),
      status: parseQueryValue(router.query?.[MY_ISSUES_MAPPER.status.key]),
      reason: parseQueryValue(router.query?.[MY_ISSUES_MAPPER.reason.key]),
      dueDate: parseQueryValue(router.query?.[MY_ISSUES_MAPPER.dueDate.key]),
    };
  }, [router.query]);

  const isCompleted = router.query.completed === 'true';

  const statusOptionsWithDisabled = useMemo(() => {
    let options = boardStatusesOptions || [];

    // Filter by selected boards if any
    if (currentFilters.board.length) {
      options = options.filter(({ boardId }) =>
        currentFilters.board.includes(boardId)
      );
    }

    // Remove duplicates and add disabled property to options that are not included in completed statuses
    return uniqBy(options, 'value').map((option) => ({
      ...option,
      disabled:
        (COMPLETED_STATUSES.includes(Number(option.value)) && !isCompleted) ||
        (!COMPLETED_STATUSES.includes(Number(option.value)) && isCompleted),
    }));
  }, [boardStatusesOptions, currentFilters.board, isCompleted]);

  const reasonOptionsWithDisabled = useMemo(() => {
    const isGenericBoardSelected = currentFilters.board.includes(
      BOARDS_ID.GENERIC_TICKETS
    );

    return GENERIC_TICKET_REASON_OPTIONS.map((option) => ({
      ...option,
      disabled: currentFilters.board.length > 0 && !isGenericBoardSelected,
    }));
  }, [currentFilters.board]);

  const filterCategories = useMemo(
    () => [
      {
        id: MY_ISSUES_MAPPER.board.key,
        label: MY_ISSUES_MAPPER.board.label,
        icon: GridViewIcon,
        options: boardsOptions,
        multiSelect: true,
      },
      {
        id: MY_ISSUES_MAPPER.status.key,
        label: MY_ISSUES_MAPPER.status.label,
        icon: RadioButtonUncheckedIcon,
        options: statusOptionsWithDisabled,
        multiSelect: true,
      },
      {
        id: MY_ISSUES_MAPPER.reason.key,
        label: MY_ISSUES_MAPPER.reason.label,
        icon: LabelOutlinedIcon,
        options: reasonOptionsWithDisabled,
        multiSelect: true,
      },
      {
        id: MY_ISSUES_MAPPER.dueDate.key,
        label: MY_ISSUES_MAPPER.dueDate.label,
        icon: CalendarTodayIcon,
        type: 'dateTime',
      },
    ],
    [boardsOptions, statusOptionsWithDisabled, reasonOptionsWithDisabled]
  );

  const error = useMemo(
    () => taskStatusesMapperError || boardsDataError,
    [taskStatusesMapperError, boardsDataError]
  );

  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error]);

  return {
    filterCategories,
    currentFilters,
  };
};

export default useMyIssuesFilterConfig;
